import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';
import { SblApprovalService } from '../../services/sbl-approval.service';

type SblApprovalStep = 'loading' | 'details' | 'otp' | 'result' | 'error';

/**
 * Public SBL advisor-approval page (route: sblApproval/:token). Mirrors the HYSA txn-approval flow:
 * load read-only transaction details, Continue → send OTP, Submit → approve with OTP.
 */
@Component({
  selector: 'app-sbl-approval',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './sbl-approval.component.html',
  styleUrl: './sbl-approval.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SblApprovalComponent implements OnInit, OnDestroy {
  token = '';
  step: SblApprovalStep = 'loading';

  /** Full get_sbl_transaction_approval_details payload. */
  details: any = null;
  canApprove = false;
  loadError = '';

  // Payment authorization (ACH) — mirrors line-of-credit's Make a Payment consent block.
  payAuthName = '';
  payAuthEffectiveDate = '';
  paySignature = '';
  payAchAuthorized = false;

  // OTP step
  otp = '';
  otpError = '';
  isResendingOtp = false;
  isSubmittingOtp = false;
  private webOtpAbortController: AbortController | null = null;

  // Final outcome
  message = '';
  status = '';
  v_tran_status = '';

  constructor(
    private route: ActivatedRoute,
    private sblApprovalService: SblApprovalService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.token = params.get('token') || '';
      this.loadApprovalDetails();
    });
  }

  ngOnDestroy(): void {
    this.stopWebOtpAutofill();
  }

  // ── Derived getters for the template ──
  get isDraw(): boolean {
    return String(this.details?.RequestType ?? '').toLowerCase().includes('draw');
  }
  get isPayment(): boolean {
    return String(this.details?.RequestType ?? '').toLowerCase().includes('payment');
  }
  get requestTypeLabel(): string {
    return this.isDraw ? 'Draw Request' : this.isPayment ? 'Payment' : (this.details?.RequestType ?? '');
  }
  get disburseToAccount(): any {
    return this.details?.Draw?.DisburseToAccount ?? null;
  }
  get payFromAccount(): any {
    return this.details?.Payment?.PayFromAccount ?? null;
  }

  /** The typed signature must match the Name on file (case-insensitive, whitespace-normalized). */
  get isSignatureMatchingName(): boolean {
    const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
    return !!this.payAuthName.trim() && normalize(this.paySignature) === normalize(this.payAuthName);
  }

  /** Shown once the client starts typing a signature that doesn't match the Name on file. */
  get signatureMismatchError(): string | null {
    if (!this.isPayment || !this.paySignature.trim() || this.isSignatureMatchingName) {
      return null;
    }
    return `Your signature must match the name on file — please type “${this.payAuthName}” exactly as shown above.`;
  }

  /** Consent satisfied: a matching signature + the authorization checkbox (only for payments). */
  get isPaymentAuthComplete(): boolean {
    return !this.isPayment || (this.isSignatureMatchingName && this.payAchAuthorized);
  }

  /** Prefill Name (best-effort from the approval payload — editable if the API doesn't carry it) and
   *  the Effective Date (today). */
  private initPaymentAuthorization(resp: any): void {
    this.payAuthName = String(
      resp?.Client?.ClientName ?? resp?.Client?.Name ??
      resp?.ApproverName ?? resp?.ClientName ?? resp?.HolderName ?? resp?.ApplicantName ??
      resp?.Holder?.Name ?? ''
    ).trim();
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    this.payAuthEffectiveDate = `${mm}/${dd}/${now.getFullYear()}`;
  }

  private loadApprovalDetails(): void {
    if (!this.token) {
      this.step = 'error';
      this.loadError = 'This approval link is missing its reference.';
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.step = 'loading';
    this.sblApprovalService.getTransactionApprovalDetails(this.token).subscribe({
      next: (data: any) => {
        const resp = this.normalize(data);
        if (!resp || String(resp.status ?? '').toUpperCase() !== 'SUCCESS') {
          this.step = 'error';
          this.loadError = resp?.message || 'This approval link is no longer valid.';
          this.changeDetectorRef.detectChanges();
          return;
        }
        this.details = resp;
        this.initPaymentAuthorization(resp);
        this.canApprove = resp.canApprove === true;
        // canApprove === false → show the server message, keep the details read-only.
        if (!this.canApprove && resp.message) {
          this.message = resp.message;
        }
        this.step = 'details';
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.step = 'error';
        this.loadError = 'This approval link is no longer valid.';
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  /** "Continue" on the details screen — send the OTP, then reveal the OTP entry. */
  onContinueClick(): void {
    if (this.isResendingOtp) {
      return;
    }
    // Payments require the client's signature + authorization before sending the OTP.
    if (this.isPayment && !this.isPaymentAuthComplete) {
      this.otpError = 'Please type your signature and check the authorization box to continue.';
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.isResendingOtp = true;
    this.otpError = '';
    this.sblApprovalService.resendApprovalOtp(this.token).subscribe({
      next: (resp: any) => {
        this.isResendingOtp = false;
        if (String(resp?.status ?? '').toUpperCase() === 'FAILED') {
          this.otpError = resp?.message || 'Unable to send a code. Please try again.';
          this.changeDetectorRef.detectChanges();
          return;
        }
        this.otp = '';
        this.step = 'otp';
        this.startWebOtpAutofill();
        this.changeDetectorRef.detectChanges();
      },
      error: (error: any) => {
        this.isResendingOtp = false;
        this.otpError = error?.error?.message || 'Unable to send a code. Please try again.';
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  resendOtp(): void {
    this.onContinueClick();
  }

  onOtpValueChange(value: string): void {
    const sanitized = (value ?? '').replace(/\D/g, '').slice(0, 6);
    if (sanitized !== value) {
      this.otp = sanitized;
    }
    this.otpError = '';
  }

  /** "Submit" — approve_sbl_request_with_otp. */
  onOtpSubmit(): void {
    if ((this.otp?.length ?? 0) < 6 || this.isSubmittingOtp) {
      this.otpError = 'Please enter the 6-digit code.';
      this.changeDetectorRef.detectChanges();
      return;
    }
    this.isSubmittingOtp = true;
    this.otpError = '';
    this.sblApprovalService.approveRequestWithOtp(this.token, this.otp).subscribe({
      next: (resp: any) => {
        this.isSubmittingOtp = false;
        if (String(resp?.v_status ?? '').toUpperCase() === 'SUCCESS') {
          this.stopWebOtpAutofill();
          this.status = 'SUCCESS';
          this.message = resp?.v_msg || 'Your request is approved.';
          this.v_tran_status = resp?.v_tran_status || 'Pending';
          this.step = 'result';
        } else {
          const msg = resp?.v_msg || "That code didn't work. Please try again.";
          // Retryable OTP errors stay on the OTP step; everything else is a final result.
          if (this.isRetryableOtpError(msg)) {
            this.otpError = msg;
          } else {
            this.status = 'FAILED';
            this.message = msg;
            this.v_tran_status = resp?.v_tran_status || '';
            this.step = 'result';
          }
        }
        this.changeDetectorRef.detectChanges();
      },
      error: (error: any) => {
        this.isSubmittingOtp = false;
        this.otpError = error?.error?.v_msg || error?.error?.message || "That code didn't work. Please try again.";
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  private isRetryableOtpError(msg: string): boolean {
    const m = (msg || '').toLowerCase();
    return m.includes('invalid or expired otp') || m.includes('too many incorrect attempts');
  }

  // ── Result banner helpers (mirrors txn-approval) ──
  isSuccessBanner(): boolean {
    if (this.status === 'SUCCESS') {
      return true;
    }
    const m = (this.message || '').toLowerCase();
    return m.includes('already processed') || m.includes('already approved') || m.includes('approved') || m.includes('completed');
  }

  isExpired(): boolean {
    return (this.message || '').toLowerCase().includes('expired');
  }

  getBannerTitle(): string {
    if (this.isExpired()) {
      return 'This approval link has expired';
    }
    return this.message;
  }

  getBannerSubtitle(): string {
    if (this.isExpired()) {
      return 'The reference below is read-only. Ask your advisor to generate a new link if action is still needed.';
    }
    if (this.isSuccessBanner()) {
      return 'The details below are for your reference.';
    }
    return 'Please contact your advisor if you need assistance with this request.';
  }

  private normalize(resp: any): any {
    let value = resp;
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        return null;
      }
    }
    if (Array.isArray(value)) {
      value = value[0];
    }
    return value ?? null;
  }

  /** WebOTP (Android Chrome) — auto-fill the SMS code when supported; safe no-op otherwise. */
  private startWebOtpAutofill(): void {
    this.stopWebOtpAutofill();
    if (!('OTPCredential' in window) || !navigator.credentials) {
      return;
    }
    const controller = new AbortController();
    this.webOtpAbortController = controller;
    (navigator.credentials as any)
      .get({ otp: { transport: ['sms'] }, signal: controller.signal })
      .then((credential: any) => {
        const code = (credential?.code ?? '').replace(/\D/g, '').slice(0, 6);
        if (code) {
          this.otp = code;
          this.otpError = '';
          this.changeDetectorRef.detectChanges();
        }
      })
      .catch(() => { /* aborted / unsupported / no matching SMS — ignore */ });
  }

  private stopWebOtpAutofill(): void {
    this.webOtpAbortController?.abort();
    this.webOtpAbortController = null;
  }
}
